import { FC, ReactNode } from 'react'

import { styled } from 'styled-components'

import { R, S } from 'core/helpers'
import { onSurface, onSurfaceVariant, outline, surface } from 'core/styles'

export type FieldGroupProps = {
  title: string
  titleRight?: ReactNode
  children: ReactNode
  className?: string
  nestLevel?: number
  variant?: 'default' | 'compact'
}

const FieldGroup: FC<FieldGroupProps> = ({
  title,
  titleRight,
  children,
  className,
  nestLevel = 0,
  variant = 'default',
}) => (
  <Container $variant={variant} className={className}>
    <TitleContainer>
      <Title $variant={variant}>{S.sentenceCase(title || '')}</Title>
      {!R.isNil(titleRight) && titleRight}
    </TitleContainer>
    <FieldGroupBox $nestLevel={nestLevel} $variant={variant}>
      {children}
    </FieldGroupBox>
  </Container>
)

export default FieldGroup

const Container = styled.div<{ $variant?: 'default' | 'compact' }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ $variant }) => ($variant === 'compact' ? onSurface : 'transparent')};
  font-size: 18px;
  font-weight: 600;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.h3<{ $variant?: 'default' | 'compact' }>`
  margin: ${(p) => (p.$variant === 'compact' ? '0' : '0 0 4px')};
  color: ${(p) => (p.$variant === 'compact' ? onSurface : onSurfaceVariant)};
  font-size: ${(p) => (p.$variant === 'compact' ? '16px' : '13px')};
  font-weight: 500;
`

export const FieldGroupBox = styled.div<{ $nestLevel?: number; $variant?: 'default' | 'compact' }>`
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;

  ${(p) =>
    p.$variant === 'default' &&
    `
      border: 1px solid ${outline};
      isolation: isolate;
      background: ${(p.$nestLevel ?? 0) === 0 ? '#ffffff' : surface};
      padding: 16px;

      &::before {
        position: absolute;
        inset: 0;
        opacity: ${(() => {
          const level = p.$nestLevel ?? 0
          return level > 0 ? Math.min(0.02 * (level - 1) + 0.02, 0.2) : 0
        })()};
        z-index: 0;
        border-radius: inherit;
        background: #000;
        content: '';
        pointer-events: none;
      }

      > * {
        position: relative;
        z-index: 1;
      }
    `}
`
